import { ReactNode } from "react";
import { SentryErrorBoundary, Sentry } from "@/lib/sentry";

interface Props {
  /** Stable identifier for the admin route, used as a Sentry tag. */
  routeId: string;
  children: ReactNode;
}

/**
 * Wraps an /dash/admin/* route in a SentryErrorBoundary so admin-only crashes
 * are captured with Replay and tagged for triage. The DashV2Layout already
 * mounts a top-level SentryErrorBoundary; this nested boundary catches the
 * error first, tags it `area: admin` + `admin_route: <routeId>`, then renders
 * an admin-flavored fallback. Resetting unmounts the failing tree without
 * blowing away the surrounding layout.
 */
export function AdminRouteBoundary({ routeId, children }: Props) {
  return (
    <SentryErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag("area", "admin");
        scope.setTag("admin_route", routeId);
        scope.setLevel("error");
      }}
      fallback={({ resetError, eventId }) => (
        <div
          role="alert"
          className="p-6 rounded-xl border border-white/10 bg-[#0e1111] text-white/80"
        >
          <p className="text-sm mb-1 font-semibold text-white">
            Admin page crashed
          </p>
          <p className="text-xs mb-3 text-white/60">
            The error was reported to Sentry with session replay
            {eventId ? ` · event ${eventId.slice(0, 8)}` : ""}.
          </p>
          <button
            type="button"
            onClick={resetError}
            className="text-xs px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20"
          >
            Try again
          </button>
        </div>
      )}
      showDialog={false}
    >
      {children}
    </SentryErrorBoundary>
  );
}

// Re-export for tests that want to verify Sentry was called with the right tags.
export { Sentry };
